import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Zap, Lightbulb, Users, Linkedin } from "lucide-react";
import NetworkBackground from "../components/effects/NetworkBackground";
import teamData from "../data/team.json";
import useLang from "../hooks/useLang";

const About = () => {
  const { t, isRTL, dir } = useLang();
  const [flippedId, setFlippedId] = useState(null);

  const values = [
    { key: "quality", icon: Shield, color: "text-blue-500 bg-blue-50" },
    { key: "transparency", icon: Zap, color: "text-yellow-500 bg-yellow-50" },
    { key: "innovation", icon: Lightbulb, color: "text-purple-500 bg-purple-50" },
    { key: "partnership", icon: Users, color: "text-green-500 bg-green-50" },
  ];

  return (
    <div dir={dir} className="font-inter">
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-dark to-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(225,29,72,0.08),transparent_70%)]" />
        <NetworkBackground fade="none" />
        <div className="container-site text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block bg-accent/10 text-accent text-sm font-semibold px-4 py-1.5 rounded-full border border-accent/30 mb-6">
              {t("about.badge")}
            </span>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">{t("about.title")}</h1>
            <p className="text-gray-300 text-xl max-w-2xl mx-auto leading-relaxed">
              {t("about.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t("about.story.title")}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t("about.story.p1")}</p>
                <p>{t("about.story.p2")}</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary to-dark rounded-3xl p-10 text-white"
            >
              <h3 className="text-xl font-semibold text-accent mb-4">{t("visionMission.visionTitle")}</h3>
              <p className="text-gray-200 text-lg leading-relaxed italic mb-6">{t("visionMission.vision")}</p>
              <h3 className="text-xl font-semibold text-accent mb-4">{t("visionMission.missionTitle")}</h3>
              <p className="text-gray-200 text-lg leading-relaxed italic">{t("visionMission.mission")}</p>
              <div className="mt-8 grid grid-cols-2 gap-5">
                {[
                  { num: "2020", label: isRTL ? "تأسست في الأردن" : "Founded in Jordan" },
                  { num: "10", label: isRTL ? "أعضاء في الفريق" : "Team Members" },
                  { num: "50+", label: isRTL ? "مشروع" : "Projects" },
                  { num: "2", label: isRTL ? "دولتان: الأردن وفلسطين" : "Countries: Jordan & Palestine" },
                ].map((item) => (
                  <div key={item.label} className="bg-white/10 rounded-xl p-4">
                    <div className="text-2xl font-bold text-accent">{item.num}</div>
                    <div className="text-gray-300 text-sm mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-4xl font-bold text-gray-900">{t("about.values.title")}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(({ key, icon: Icon, color }, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-center"
              >
                <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <Icon size={24} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  {t(`about.values.${key}.title`)}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t(`about.values.${key}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-block bg-accent/10 text-accent text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              {t("about.team.badge")}
            </span>
            <h2 className="text-4xl font-bold text-gray-900">{t("about.team.title")}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 max-w-3xl mx-auto">
            {teamData.map((member, i) => {
              const name = isRTL ? member.nameAr : member.name;
              const isFlipped = flippedId === member.id;
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="group h-80"
                  style={{ perspective: 1200 }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={isFlipped}
                    aria-label={name}
                    onClick={() => setFlippedId(isFlipped ? null : member.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setFlippedId(isFlipped ? null : member.id);
                      }
                    }}
                    className={`relative w-full h-full cursor-pointer transition-transform duration-700 group-hover:[transform:rotateY(180deg)] ${
                      isFlipped ? "[transform:rotateY(180deg)]" : ""
                    }`}
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    {/* Front */}
                    <div
                      className="absolute inset-0 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center p-6"
                      style={{ backfaceVisibility: "hidden" }}
                    >
                      <img
                        src={member.photo}
                        alt={name}
                        className="w-28 h-28 rounded-full object-cover mb-5 ring-4 ring-accent/20"
                      />
                      <h3 className="text-xl font-bold text-gray-900">{name}</h3>
                      <p className="text-accent text-sm font-semibold mt-1">
                        {isRTL ? member.roleAr : member.role}
                      </p>
                    </div>

                    {/* Back */}
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-primary to-dark rounded-2xl flex flex-col items-center justify-center text-center p-7 [transform:rotateY(180deg)]"
                      style={{ backfaceVisibility: "hidden" }}
                    >
                      <h3 className="text-lg font-bold text-white">{name}</h3>
                      <p className="text-accent text-xs font-semibold mt-0.5 mb-4">
                        {isRTL ? member.roleAr : member.role}
                      </p>
                      <p className="text-gray-300 text-sm leading-relaxed mb-5">
                        {isRTL ? member.bioAr : member.bio}
                      </p>
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-2 bg-accent/15 text-accent text-sm font-medium px-4 py-2 rounded-lg hover:bg-accent/25 transition-colors"
                      >
                        <Linkedin size={16} /> LinkedIn
                      </a>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
